import { useContext } from "react";
import { useLocalization } from "@fluent/react";
import ReactMarkdown from "react-markdown";
import { Dialog } from "cerulean";
import { type LimitsForDisplayType } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import InstitutionSettingsContext from "../../contexts/InstitutionSettingsContext";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { RenderLimit } from "../LimitsDialog";

type LimitsAndDisclosuresDialog = {
  isOpen: boolean;
  handleClose: () => void;
  limits: LimitsForDisplayType;
  fromFlow?: "bulkPayment";
};

const LimitsAndDisclosuresDialog = ({
  isOpen,
  handleClose,
  limits,
  fromFlow,
}: LimitsAndDisclosuresDialog) => {
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const {
    ach_payment_disclosure: achPaymentDisclosure,
    ach_collect_payment_disclosure: achCollectPaymentDisclosure,
    long_name: bankName,
  } = useContext(InstitutionSettingsContext);
  const { ach_payments_collect: achPaymentsCollectFeature } = features;
  const fromBulkFlow = fromFlow === "bulkPayment";
  const fednowEnabled = features?.expedited_payments;

  const stripLimits = (limitsForDisplay: LimitsForDisplayType) =>
    Object.fromEntries(
      Object.entries(limitsForDisplay).map(([transactionType, values]) => {
        // for each transactionType, filter the object
        const filteredValues = Object.fromEntries(
          Object.entries(values).filter(([key]) =>
            // keep the "amount_available" for numeric keys;
            Number(key),
          ),
        );

        return [transactionType, filteredValues];
      }),
    );

  const limitsForDisplay = stripLimits(limits);

  const renderRemainingUnverifiedACHLimits = () => (
    <div className="margin--bottom--l">
      <div className="fontColor--heading fontWeight--bold">ACH send limits</div>
      <div className="margin--bottom--s">
        You’re permitted to send the following amount from your {bankName}{" "}
        account:
      </div>
      {limitsForDisplay?.organization_user_unverified_ach_push && (
        <>
          <div className="fontColor--heading">
            {l10n.getString("limit-personal-title")}
          </div>
          <RenderLimit
            limits={limitsForDisplay}
            transactionType="organization_user_unverified_ach_push"
          />
          <br />
        </>
      )}
      {limitsForDisplay?.unverified_ach_push && (
        <>
          <div className="fontColor--heading">
            {l10n.getString("limit-organizational-title")}
          </div>
          <RenderLimit
            limits={limitsForDisplay}
            transactionType="unverified_ach_push"
          />
        </>
      )}
    </div>
  );

  const renderRemainingCollectACHLimits = () => (
    <div className="margin--bottom--l">
      <div className="fontColor--heading fontWeight--bold">
        ACH collect limits
      </div>
      <div className="margin--bottom--s">
        You’re permitted to collect the following amount to your {bankName}{" "}
        account:
      </div>
      {limitsForDisplay?.organization_user_unverified_ach_pull && (
        <>
          <div className="fontColor--heading">
            {l10n.getString("limit-personal-title")}
          </div>
          <RenderLimit
            limits={limitsForDisplay}
            transactionType="organization_user_unverified_ach_pull"
          />
          <br />
        </>
      )}
      {limitsForDisplay?.unverified_ach_pull && (
        <>
          <div className="fontColor--heading">
            {l10n.getString("limit-organizational-title")}
          </div>
          <RenderLimit
            limits={limitsForDisplay}
            transactionType="unverified_ach_pull"
          />
        </>
      )}
    </div>
  );

  const renderRemainingFedNowLimits = () => {
    // Only showing organizational `fednow_unverified` limits for now.
    // There's currently no way to configure a `organization_user_fednow_unverified` limit as an org admin
    if (!limitsForDisplay?.fednow_unverified) return null;

    return (
      <div className="margin--bottom--l">
        <div className="fontColor--heading fontWeight--bold">
          {l10n.getString("fednow-limits-heading")}
        </div>
        <div className="margin--bottom--s">
          You’re permitted to instantly send the following amount from your{" "}
          {bankName} account:
        </div>
        {limitsForDisplay?.organization_user_fednow_unverified && (
          <>
            <div className="fontColor--heading">
              {l10n.getString("limit-personal-title")}
            </div>
            <RenderLimit
              limits={limitsForDisplay}
              transactionType="organization_user_fednow_unverified"
            />
            <br />
          </>
        )}
        {limitsForDisplay?.fednow_unverified && (
          <>
            <div className="fontColor--heading">
              {l10n.getString("limit-organizational-title")}
            </div>
            <RenderLimit
              limits={limitsForDisplay}
              transactionType="fednow_unverified"
            />
          </>
        )}
      </div>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={handleClose}
      title={l10n.getString("ach-payment-limits-modal-title")}
    >
      {/* ach send  */}
      {renderRemainingUnverifiedACHLimits()}
      {achPaymentDisclosure ? (
        <div className="margin--bottom--l">
          <div className="fontColor--heading fontWeight--bold">
            {l10n.getString("send-disclosures-heading")}
          </div>
          <ReactMarkdown >
            {achPaymentDisclosure}
          </ReactMarkdown>
        </div>
      ) : null}

      {/* fed now */}
      {fromBulkFlow && fednowEnabled && renderRemainingFedNowLimits()}

      {/* ach collect */}
      {achPaymentsCollectFeature ? (
        <>
          {renderRemainingCollectACHLimits()}
          {achCollectPaymentDisclosure ? (
            <div className="margin--bottom--l">
              <div className="fontColor--heading fontWeight--bold">
                {l10n.getString("collect-disclosures-heading")}
              </div>
              <ReactMarkdown >
                {achCollectPaymentDisclosure}
              </ReactMarkdown>
            </div>
          ) : null}
        </>
      ) : null}
    </Dialog>
  );
};

export default LimitsAndDisclosuresDialog;
